import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Cog,
  Terminal,
  Users,
  Star,
  GitFork,
  Folder,
  File,
  FolderOpen,
  Check,
  X,
  Sparkles,
  BookOpen,
  Layers,
} from 'lucide-react';
import clsx from 'clsx';

/* ------------------------------------------------------------------ */
/*  Utility: fade-in-on-scroll wrapper                                */
/* ------------------------------------------------------------------ */
function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
      {/* Background effects */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-40" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-accent/5 rounded-full blur-[120px]" />
      <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium mb-8">
                <Sparkles className="h-3.5 w-3.5" />
                Engineering Governance Platform
              </div>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Bring AI Under{' '}
              <span className="text-gradient">Engineering Governance</span>
            </motion.h1>

            <motion.p
              className="text-lg text-gray-400 leading-relaxed mb-10 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Your developers use AI to ship faster. CodeForge makes sure every
              AI-assisted change follows your enterprise architecture, coding standards,
              and compliance requirements — automatically.
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/wizard" className="btn-primary text-base px-8 py-4">
                Build Your Blueprint
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/templates" className="btn-ghost text-base px-8 py-4">
                Explore Templates
              </Link>
            </motion.div>
          </div>

          {/* Right: Terminal mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-accent/10 via-purple-500/10 to-accent/10 rounded-2xl blur-xl" />
              <div className="relative rounded-xl border border-dark-border bg-surface-DEFAULT overflow-hidden shadow-2xl">
                {/* Terminal header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-dark-border bg-surface-50/50">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="ml-2 text-xs text-gray-500 font-mono">
                    Development Blueprint -- generated by CodeForge
                  </span>
                </div>
                {/* Terminal content */}
                <div className="p-5 font-mono text-sm leading-relaxed overflow-hidden">
                  <div className="text-accent font-semibold mb-2"># CLAUDE.md</div>
                  <div className="text-gray-500 mb-3">
                    # Generated by CodeForge for Acme Corp
                  </div>
                  <div className="text-blue-300 font-semibold mb-1">
                    ## Project Overview
                  </div>
                  <div className="text-gray-400 mb-3 text-xs">
                    E-commerce platform built with Next.js 14, TypeScript,
                    <br />
                    PostgreSQL, and deployed on AWS via Terraform.
                  </div>
                  <div className="text-blue-300 font-semibold mb-1">
                    ## Code Standards
                  </div>
                  <div className="text-gray-400 text-xs mb-1">
                    - <span className="text-green-400">ESLint</span> with
                    @typescript-eslint/strict
                  </div>
                  <div className="text-gray-400 text-xs mb-1">
                    - <span className="text-green-400">Prettier</span> with 2-space
                    indent, single quotes
                  </div>
                  <div className="text-gray-400 text-xs mb-1">
                    - Test coverage minimum:{' '}
                    <span className="text-amber-400">80%</span>
                  </div>
                  <div className="text-gray-400 text-xs mb-3">
                    - Max file length:{' '}
                    <span className="text-amber-400">300 lines</span>
                  </div>
                  <div className="text-blue-300 font-semibold mb-1">
                    ## Sensitive Paths
                  </div>
                  <div className="text-red-400 text-xs mb-1">
                    - <span className="text-red-400/80">NEVER</span> modify
                    auth/*, payments/*, infra/*
                  </div>
                  <div className="text-gray-400 text-xs">
                    - Always require human review for these paths
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <div className="w-2 h-4 bg-accent animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#problem"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        <span className="text-xs font-medium tracking-wider uppercase">
          Discover what&apos;s inside
        </span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="h-5 w-5" />
        </motion.div>
      </motion.a>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Problem Section: "The AI Coding Gap"                               */
/* ------------------------------------------------------------------ */
function ProblemSection() {
  return (
    <section id="problem" className="relative py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            The AI Coding <span className="text-gradient">Gap</span>
          </h2>
          <p className="section-subheading mx-auto">
            Two developers. Same project. Same AI. Wildly different output.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Developer A */}
            <div className="card border-red-500/20">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-sm font-medium text-red-400">
                  Developer A -- No Guidelines
                </span>
              </div>
              <div className="rounded-lg bg-[#0d1117] p-4 font-mono text-xs overflow-x-auto">
                <pre className="text-gray-300">
{`// UserService.js - 847 lines, no types
const axios = require('axios')
var userCache = {}

function getUser(id, cb) {
  if (userCache[id]) {
    cb(null, userCache[id])
    return
  }
  axios.get('/api/users/' + id)
    .then(function(res) {
      userCache[id] = res.data
      cb(null, res.data)
    })
    .catch(function(err) {
      console.log(err) // TODO: handle error
      cb(err)
    })
}

// Directly modifies payment logic
function chargeUser(userId, amount) {
  // No validation, no auth check
  return axios.post('/payments/charge', {
    user: userId, amount: amount
  })
}`}</pre>
              </div>
            </div>

            {/* Developer B */}
            <div className="card border-green-500/20">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm font-medium text-green-400">
                  Developer B -- With CodeForge Blueprint
                </span>
              </div>
              <div className="rounded-lg bg-[#0d1117] p-4 font-mono text-xs overflow-x-auto">
                <pre className="text-gray-300">
{`// user.service.ts - Clean, typed, tested
import { injectable } from 'tsyringe';
import type { User } from '@/types/user';
import { UserRepository } from '@/repos/user.repo';
import { CacheService } from '@/services/cache';
import { AppError } from '@/lib/errors';

@injectable()
export class UserService {
  constructor(
    private readonly users: UserRepository,
    private readonly cache: CacheService
  ) {}

  async getUser(id: string): Promise<User> {
    const cached = await this.cache.get<User>(id);
    if (cached) return cached;

    const user = await this.users.findById(id);
    if (!user) throw new AppError('NOT_FOUND');

    await this.cache.set(id, user, 300);
    return user;
  }
  // payments/* is a SENSITIVE PATH - flagged
}`}</pre>
              </div>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div className="border-l-4 border-accent pl-6 py-4 max-w-3xl mx-auto">
            <p className="text-xl text-white font-medium leading-relaxed">
              Without guardrails, AI amplifies chaos at machine speed.
            </p>
            <p className="text-gray-400 mt-2">
              The same model that produces perfect code for one developer generates
              anti-patterns for another -- simply because it lacks context about your
              team&apos;s standards.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Solution Section: "The Missing Layer"                              */
/* ------------------------------------------------------------------ */
const solutionRows = [
  {
    title: 'Development standards & guardrails',
    before: 'Generic AI with no project context',
    after: 'AI that enforces your architecture, patterns, and coding standards',
  },
  {
    title: 'Unified tool access',
    before: 'Manual tool integration, different for each developer',
    after: 'Standardized toolchain access across the entire organization',
  },
  {
    title: 'Automated compliance checks',
    before: 'No pre-commit enforcement, inconsistent formatting',
    after: 'Automated quality gates that catch violations before code ships',
  },
  {
    title: 'Deployment pipeline specifications',
    before: 'Copy-pasted configs with unknown provenance',
    after: 'Generated pipelines matching your exact stack and security policies',
  },
  {
    title: 'Structured review process',
    before: 'Free-form reviews with missing context',
    after: 'Structured templates with compliance checklists and AI-assisted review',
  },
  {
    title: 'Role-based developer guardrails',
    before: 'One-size-fits-all AI behavior across all seniority levels',
    after: 'Seniority-aware permissions, review requirements, and escalation paths',
  },
  {
    title: 'Machine-readable code specifications',
    before: 'Tribal knowledge buried in Confluence pages',
    after: 'Codified standards that AI actually reads and follows',
  },
  {
    title: 'Phase-by-phase project methodology',
    before: 'Ad-hoc development with no structure or milestones',
    after: 'Sequenced delivery phases with deliverables, quality gates, and sign-offs',
  },
  {
    title: 'Ready-to-use prompt library',
    before: 'Every developer invents their own prompts from scratch',
    after: '180+ curated prompts matched to your project phase and standards',
  },
];

function SolutionSection() {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section className="relative py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.02] to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            The <span className="text-gradient">Missing Layer</span>
          </h2>
          <p className="section-subheading mx-auto">
            CodeForge generates an organizational blueprint that standardizes how your team builds with AI.
          </p>
        </FadeIn>

        {/* Interactive table */}
        <FadeIn delay={0.1}>
          <div className="card p-0 overflow-hidden mb-16">
            <div className="grid grid-cols-[1fr,1fr,1fr] text-sm font-medium text-gray-400 border-b border-dark-border">
              <div className="px-6 py-4">What you get</div>
              <div className="px-6 py-4 border-l border-dark-border">
                <span className="flex items-center gap-1.5">
                  <X className="h-3.5 w-3.5 text-red-400" /> Without CodeForge
                </span>
              </div>
              <div className="px-6 py-4 border-l border-dark-border">
                <span className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-green-400" /> With CodeForge
                </span>
              </div>
            </div>
            {solutionRows.map((row, i) => (
              <div key={i}>
                <button
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="w-full grid grid-cols-[1fr,1fr,1fr] text-sm border-b border-dark-border hover:bg-dark-hover transition-colors text-left"
                >
                  <div className="px-6 py-4 flex items-center gap-2 text-white font-medium">
                    {expanded === i ? (
                      <ChevronDown className="h-4 w-4 text-accent shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                    )}
                    {row.title}
                  </div>
                  <div className="px-6 py-4 border-l border-dark-border text-gray-500">
                    {row.before}
                  </div>
                  <div className="px-6 py-4 border-l border-dark-border text-gray-300">
                    {row.after}
                  </div>
                </button>
                {expanded === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-b border-dark-border bg-dark-hover/50"
                  >
                    <div className="grid grid-cols-2 gap-4 p-6">
                      <div className="rounded-lg bg-[#0d1117] border border-red-500/10 p-4">
                        <div className="text-xs text-red-400 font-medium mb-2 uppercase tracking-wider">
                          Before
                        </div>
                        <p className="text-sm text-gray-400">{row.before}</p>
                      </div>
                      <div className="rounded-lg bg-[#0d1117] border border-green-500/10 p-4">
                        <div className="text-xs text-green-400 font-medium mb-2 uppercase tracking-wider">
                          After
                        </div>
                        <p className="text-sm text-gray-300">{row.after}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </FadeIn>

        {/* Flow diagram */}
        <FadeIn delay={0.2}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ClipboardList, label: 'Define', desc: 'Specify your standards and structure' },
              { icon: Cog, label: 'Generate', desc: 'We build your organizational blueprint' },
              { icon: Terminal, label: 'Deploy', desc: 'Apply the blueprint to your repo' },
              { icon: Users, label: 'Align', desc: 'Every developer, same playbook' },
            ].map((step, i) => (
              <div key={step.label} className="relative">
                <div className="card card-hover text-center py-8 h-full">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 mb-4">
                    <step.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">{step.label}</h3>
                  <p className="text-sm text-gray-500">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2 z-10 -translate-y-1/2">
                    <ChevronRight className="h-4 w-4 text-surface-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  What You Get: File Tree                                            */
/* ------------------------------------------------------------------ */
interface TreeItem {
  name: string;
  type: 'folder' | 'file';
  children?: TreeItem[];
  preview?: string;
}

const outputTree: TreeItem[] = [
  { name: 'CLAUDE.md', type: 'file', preview: '# Project context, standards, and guardrails for Claude' },
  {
    name: '.claude/',
    type: 'folder',
    children: [
      { name: 'settings.json', type: 'file', preview: '{ "permissions": { ... }, "plugins": [ ... ] }' },
      {
        name: 'commands/',
        type: 'folder',
        children: [
          { name: 'plan.md', type: 'file', preview: '/plan — Generate an implementation plan' },
          { name: 'tdd.md', type: 'file' },
          { name: 'code-review.md', type: 'file' },
          { name: 'phase.md', type: 'file', preview: '/phase — Check or advance the current project phase' },
          { name: 'verify.md', type: 'file' },
        ],
      },
      {
        name: 'agents/',
        type: 'folder',
        children: [
          { name: 'planner.md', type: 'file', preview: 'Specialized subagent for implementation planning' },
          { name: 'code-reviewer.md', type: 'file' },
          { name: 'architect.md', type: 'file' },
        ],
      },
      {
        name: 'rules/',
        type: 'folder',
        children: [
          { name: 'common/', type: 'folder', children: [
            { name: 'coding-style.md', type: 'file', preview: 'Auto-loaded coding standards for every session' },
            { name: 'security.md', type: 'file' },
            { name: 'testing.md', type: 'file' },
            { name: 'git-workflow.md', type: 'file' },
          ] },
        ],
      },
    ],
  },
  { name: '.mcp.json', type: 'file', preview: '{ "mcpServers": { "jira": { ... }, "github": { ... } } }' },
  {
    name: '.github/',
    type: 'folder',
    children: [
      {
        name: 'workflows/',
        type: 'folder',
        children: [
          { name: 'ci.yml', type: 'file', preview: 'name: CI\\non: [push, pull_request]\\njobs: ...' },
          { name: 'ai-code-audit.yml', type: 'file' },
        ],
      },
      { name: 'PULL_REQUEST_TEMPLATE.md', type: 'file' },
      { name: 'CODEOWNERS', type: 'file', preview: '*.ts @team-leads\\npayments/* @security-team' },
    ],
  },
  {
    name: 'git-hooks/',
    type: 'folder',
    children: [
      { name: 'pre-commit', type: 'file' },
      { name: 'commit-msg', type: 'file' },
      { name: 'pre-push', type: 'file' },
    ],
  },
  {
    name: 'playbook/',
    type: 'folder',
    children: [
      { name: 'PLAYBOOK.md', type: 'file', preview: '# Phase timeline, status table, and how to use the playbook' },
      {
        name: 'phases/',
        type: 'folder',
        children: [
          { name: '01-project-setup.md', type: 'file', preview: '## Objectives, deliverables, quality gates' },
          { name: '02-core-architecture.md', type: 'file' },
          { name: '...', type: 'file' },
        ],
      },
      {
        name: 'prompts/',
        type: 'folder',
        children: [
          { name: '01/', type: 'folder', children: [
            { name: 'scaffold-project.md', type: 'file', preview: 'Ready-to-paste prompt for Claude Code' },
          ] },
        ],
      },
      {
        name: 'claude-overrides/',
        type: 'folder',
        children: [
          { name: '01-project-setup.claude.md', type: 'file', preview: 'Phase-specific DO / DO NOT rules for Claude' },
        ],
      },
      { name: 'codeforge-phase.sh', type: 'file', preview: './codeforge-phase.sh status | next | pause | resume | reset' },
    ],
  },
  {
    name: 'plugin/',
    type: 'folder',
    children: [
      {
        name: 'codeforge-{project}/',
        type: 'folder',
        children: [
          { name: '.claude-plugin/plugin.json', type: 'file', preview: 'Distributable plugin metadata' },
          { name: 'skills/', type: 'folder', children: [
            { name: 'plan/SKILL.md', type: 'file' },
            { name: 'phase/SKILL.md', type: 'file' },
          ] },
          { name: 'agents/', type: 'folder' },
          { name: 'hooks/hooks.json', type: 'file' },
        ],
      },
    ],
  },
  {
    name: 'docs/',
    type: 'folder',
    children: [
      { name: 'ONBOARDING.md', type: 'file' },
      { name: 'AI-USAGE-POLICY.md', type: 'file' },
      { name: 'CONTRIBUTING.md', type: 'file' },
      { name: 'ARCHITECTURE.md', type: 'file' },
    ],
  },
  { name: '.eslintrc.js', type: 'file' },
  { name: '.prettierrc', type: 'file' },
  { name: 'install.sh', type: 'file', preview: 'One-command installer: copies all files into your repo' },
];

function TreeNode({ item, depth = 0 }: { item: TreeItem; depth?: number }) {
  const [open, setOpen] = useState(depth === 0);
  const [showPreview, setShowPreview] = useState(false);
  const isFolder = item.type === 'folder';

  return (
    <div>
      <button
        onClick={() => {
          if (isFolder) setOpen(!open);
          else if (item.preview) setShowPreview(!showPreview);
        }}
        className={clsx(
          'flex items-center gap-2 w-full text-left px-2 py-1 rounded text-sm transition-colors',
          'hover:bg-surface-100/50',
          item.preview && !isFolder && 'cursor-pointer'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {isFolder ? (
          open ? (
            <FolderOpen className="h-4 w-4 text-accent shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-accent shrink-0" />
          )
        ) : (
          <File className="h-4 w-4 text-gray-500 shrink-0" />
        )}
        <span className={clsx(isFolder ? 'text-accent-300 font-medium' : 'text-gray-400')}>
          {item.name}
        </span>
      </button>
      {showPreview && item.preview && (
        <div
          className="ml-8 mt-1 mb-2 p-3 rounded-lg bg-[#0d1117] border border-dark-border font-mono text-xs text-gray-400"
          style={{ marginLeft: `${depth * 16 + 32}px` }}
        >
          {item.preview}
        </div>
      )}
      {isFolder && open && item.children?.map((child, i) => (
        <TreeNode key={i} item={child} depth={depth + 1} />
      ))}
    </div>
  );
}

function WhatYouGetSection() {
  return (
    <section className="relative py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            What You <span className="text-gradient">Get</span>
          </h2>
          <p className="section-subheading mx-auto">
            A single downloadable package containing your complete organizational
            blueprint -- standards, guardrails, methodology, and automation. Click to explore what&apos;s inside.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="max-w-2xl mx-auto">
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-dark-border">
                <Folder className="h-4 w-4 text-accent" />
                <span className="text-sm font-medium text-white">your-project/</span>
                <span className="text-xs text-gray-500 ml-auto">Generated output</span>
              </div>
              {outputTree.map((item, i) => (
                <TreeNode key={i} item={item} />
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Template Gallery Preview                                           */
/* ------------------------------------------------------------------ */
const previewTemplates = [
  {
    name: 'Next.js Enterprise',
    category: 'Frontend',
    categoryColor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    description: 'Production-ready Next.js 14 with TypeScript, Tailwind, Prisma, and comprehensive testing setup.',
    techStack: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind'],
    stars: 342,
    forks: 89,
  },
  {
    name: 'Spring Boot Microservices',
    category: 'Backend',
    categoryColor: 'bg-green-500/10 text-green-400 border-green-500/20',
    description: 'Java/Kotlin microservice architecture with Spring Boot, Kafka, and Kubernetes deployment.',
    techStack: ['Java', 'Spring Boot', 'Kafka', 'K8s'],
    stars: 256,
    forks: 67,
  },
  {
    name: 'Python ML Pipeline',
    category: 'Data/ML',
    categoryColor: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    description: 'Machine learning project setup with PyTorch, MLflow, DVC, and automated experiment tracking.',
    techStack: ['Python', 'PyTorch', 'MLflow', 'DVC'],
    stars: 198,
    forks: 45,
  },
  {
    name: 'Django SaaS',
    category: 'Full Stack',
    categoryColor: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    description: 'Multi-tenant SaaS boilerplate with Django, Celery, Redis, and Stripe integration.',
    techStack: ['Python', 'Django', 'Celery', 'Redis'],
    stars: 187,
    forks: 52,
  },
];

function TemplateGalleryPreview() {
  return (
    <section className="relative py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.02] to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            Start from a <span className="text-gradient">Template</span>
          </h2>
          <p className="section-subheading mx-auto">
            Battle-tested blueprints from the community. Adopt, customize, and
            standardize.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {previewTemplates.map((tpl) => (
              <div
                key={tpl.name}
                className="card card-hover group flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <span
                    className={clsx('badge border', tpl.categoryColor)}
                  >
                    {tpl.category}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {tpl.stars}
                    </span>
                    <span className="flex items-center gap-1">
                      <GitFork className="h-3 w-3" />
                      {tpl.forks}
                    </span>
                  </div>
                </div>
                <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">
                  {tpl.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4 flex-1 line-clamp-2">
                  {tpl.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {tpl.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-0.5 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.2} className="text-center mt-10">
          <Link
            to="/templates"
            className="inline-flex items-center gap-2 text-accent hover:text-accent-400 font-medium transition-colors"
          >
            Browse all templates
            <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Two Pillars: Environment + Playbooks                               */
/* ------------------------------------------------------------------ */
const playbookExamples = [
  { icon: '\u{1F331}', name: 'Greenfield Application', phases: 7 },
  { icon: '\u{1F916}', name: 'ML / Data Science Pipeline', phases: 7 },
  { icon: '\u{1F517}', name: 'API Platform', phases: 7 },
  { icon: '\u{1F3D7}\u{FE0F}', name: 'Microservices Architecture', phases: 7 },
  { icon: '\u{2601}\u{FE0F}', name: 'Cloud Migration', phases: 8 },
  { icon: '\u{1F4F1}', name: 'Mobile Application', phases: 7 },
  { icon: '\u{1F9F1}', name: 'Legacy Modernization', phases: 8 },
  { icon: '\u{1F680}', name: 'DevOps Platform', phases: 7 },
];

function TwoPillarsSection() {
  return (
    <section className="relative py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.02] to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            Two Pillars, <span className="text-gradient">One Package</span>
          </h2>
          <p className="section-subheading mx-auto">
            CodeForge generates a unified blueprint that combines two
            complementary systems. Together they govern every aspect of your
            team&apos;s AI-assisted development: <em className="text-white not-italic">how</em> code
            gets written and <em className="text-white not-italic">what</em> gets built next.
          </p>
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-6 mb-16">
          {/* Pillar 1: Environment Config */}
          <FadeIn delay={0.1}>
            <div className="card h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <Cog className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">The HOW</h3>
                  <p className="text-xs text-gray-500">Environment Configuration</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                Organizational specifications that govern <em className="text-gray-300 not-italic">how</em> your
                team writes code: coding standards, architecture patterns, protected
                boundaries, review requirements, and tool access controls.
              </p>
              <div className="space-y-2">
                {[
                  { file: 'CLAUDE.md', desc: 'Project context, standards, and guardrails' },
                  { file: '.mcp.json', desc: 'Standardized tool access for every developer' },
                  { file: 'git-hooks/*', desc: 'Pre-commit, commit-msg, and pre-push checks' },
                  { file: 'workflows/ci.yml', desc: 'CI/CD pipeline matching your exact stack' },
                  { file: '.claude/rules/*', desc: 'Auto-loaded coding standards and security rules' },
                  { file: 'docs/*', desc: 'Onboarding, AI policy, architecture, contributing' },
                ].map((item) => (
                  <div key={item.file} className="flex items-start gap-2 text-xs">
                    <span className="font-mono text-accent shrink-0 mt-0.5">{item.file}</span>
                    <span className="text-gray-500">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Pillar 2: Playbooks */}
          <FadeIn delay={0.2}>
            <div className="card h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">The WHAT</h3>
                  <p className="text-xs text-gray-500">Project Playbooks</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                A structured development methodology that defines <em className="text-gray-300 not-italic">what</em> gets
                built and in what sequence: objectives, deliverables, quality gates,
                and ready-to-use prompts for each phase.
              </p>
              <div className="space-y-2">
                {[
                  { file: 'PLAYBOOK.md', desc: 'Phase timeline, status tracking, and usage guide' },
                  { file: 'phases/*.md', desc: 'Objectives, deliverables, and quality gates per phase' },
                  { file: 'prompts/**/*.md', desc: '180+ curated prompts matched to each phase' },
                  { file: 'claude-overrides/*.md', desc: 'Phase-specific DO / DO NOT rules for Claude' },
                  { file: 'codeforge-phase.sh', desc: 'CLI to switch phases, pause/resume, and reset' },
                ].map((item) => (
                  <div key={item.file} className="flex items-start gap-2 text-xs">
                    <span className="font-mono text-purple-400 shrink-0 mt-0.5">{item.file}</span>
                    <span className="text-gray-500">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Woven together */}
        <FadeIn delay={0.3}>
          <div className="border-l-4 border-accent pl-6 py-4 max-w-3xl mx-auto mb-16">
            <p className="text-lg text-white font-medium leading-relaxed">
              The standards and methodology reinforce each other.
            </p>
            <p className="text-gray-400 mt-2">
              When your team advances to a new phase, the blueprint automatically
              adapts AI behavior to focus on that phase&apos;s deliverables -- while your
              organizational standards remain enforced throughout. Governance and
              execution work together, not in isolation.
            </p>
          </div>
        </FadeIn>

        {/* Playbook examples */}
        <FadeIn delay={0.1}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium">
              <Layers className="h-3.5 w-3.5" />
              10 Playbooks Included
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {playbookExamples.map((pb) => (
              <Link
                key={pb.name}
                to="/playbooks"
                className="card card-hover group flex flex-col items-center text-center py-6"
              >
                <span className="text-2xl mb-2">{pb.icon}</span>
                <h3 className="text-white font-semibold mb-1 group-hover:text-accent transition-colors text-sm">
                  {pb.name}
                </h3>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Layers className="w-3 h-3" />
                  {pb.phases} phases
                </span>
              </Link>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.2} className="text-center mt-10">
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-2 text-accent hover:text-accent-400 font-medium transition-colors"
          >
            Browse all playbooks
            <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  How It Works                                                       */
/* ------------------------------------------------------------------ */
function HowItWorksSection() {
  const steps = [
    {
      icon: ClipboardList,
      num: '01',
      title: 'Define your standards',
      desc: 'Walk through our guided wizard covering your team structure, coding standards, architecture policies, and project methodology.',
    },
    {
      icon: Cog,
      num: '02',
      title: 'We generate your blueprint',
      desc: 'CodeForge produces a complete organizational blueprint: development standards (the HOW) and a phased project methodology (the WHAT).',
    },
    {
      icon: Terminal,
      num: '03',
      title: 'Deploy to your repository',
      desc: 'Apply the blueprint to your repo with a single command. Track your team\'s progress through each delivery phase.',
    },
    {
      icon: Users,
      num: '04',
      title: 'Every developer, aligned',
      desc: 'Your entire team follows the same specifications, meets the same quality gates, and delivers in the same sequence -- fully aligned, fully compliant.',
    },
  ];

  return (
    <section className="relative py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-16">
          <h2 className="section-heading mb-4">
            How It <span className="text-gradient">Works</span>
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <FadeIn key={step.num} delay={i * 0.1}>
              <div className="relative">
                <div className="text-5xl font-black text-surface-100 mb-4">
                  {step.num}
                </div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 mb-4">
                  <step.icon className="h-6 w-6 text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Final CTA                                                          */
/* ------------------------------------------------------------------ */
function FinalCTA() {
  return (
    <section className="relative py-32">
      <div className="absolute inset-0 bg-gradient-to-t from-accent/[0.04] to-transparent" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6 leading-tight">
            Your team deserves a blueprint,{' '}
            <span className="text-gradient">not a blank canvas</span>
          </h2>
          <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            Stop hoping for consistency and start engineering it.
            Generate your organizational blueprint for AI-assisted development in minutes.
          </p>
          <Link to="/wizard" className="btn-primary text-lg px-10 py-4">
            Build Your Blueprint
            <ArrowRight className="h-5 w-5" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Landing Page                                                       */
/* ------------------------------------------------------------------ */
export default function LandingPage() {
  return (
    <div className="relative">
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <WhatYouGetSection />
      <TemplateGalleryPreview />
      <TwoPillarsSection />
      <HowItWorksSection />
      <FinalCTA />

      {/* Footer */}
      <footer className="border-t border-dark-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <span className="font-semibold text-white">
                Code<span className="text-accent">Forge</span>
              </span>
              <span>&middot;</span>
              <span>AI Development Blueprint Platform</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/templates" className="hover:text-gray-300 transition-colors">
                Templates
              </Link>
              <Link to="/wizard" className="hover:text-gray-300 transition-colors">
                Wizard
              </Link>
              <a href="https://github.com" className="hover:text-gray-300 transition-colors">
                GitHub
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
